import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { detectPortal } from "@/lib/portal";
import { http } from "@/lib/api";
import { AppLayout } from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Organizations from "@/pages/Organizations";
import Users from "@/pages/Users";
import Advertisers from "@/pages/Advertisers";
import Products from "@/pages/Products";
import Offers from "@/pages/Offers";
import OfferEditorPage from "@/pages/OfferEditorPage";
import Streams from "@/pages/Streams";
import MultiLinks from "@/pages/MultiLinks";
import MetaIntegrations from "@/pages/MetaIntegrations";
import Requests from "@/pages/Requests";
import Logs from "@/pages/Logs";
import Clicks from "@/pages/Clicks";
import Conversions from "@/pages/Conversions";
import Statistics from "@/pages/Statistics";
import Finance from "@/pages/Finance";
import Wallets from "@/pages/Wallets";
import Announcements from "@/pages/News";
import Postbacks from "@/pages/Postbacks";
import Settings from "@/pages/Settings";
import System from "@/pages/System";
import OrganizationDetail from "@/pages/OrganizationDetail";
import SuperBilling from "@/pages/SuperBilling";
import PortalEntry, { PendingView } from "@/pages/Portal";
import Landing from "@/pages/Landing";
import Maintenance from "@/pages/Maintenance";
import { BrandApplier } from "@/lib/branding";

function Loader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white" />
    </div>
  );
}

// Backward-compat: old /register/:slug links now point at the affiliate portal.
function RegisterRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/portal/${slug}/register`} replace />;
}

function PortalRoutes() {
  return (
    <Routes>
      <Route path="/portal/:slug" element={<PortalEntry page="login" />} />
      <Route path="/portal/:slug/login" element={<PortalEntry page="login" />} />
      <Route path="/portal/:slug/register" element={<PortalEntry page="register" />} />
      <Route path="/portal/:slug/forgot-password" element={<PortalEntry page="forgot" />} />
      <Route path="/register/:slug" element={<RegisterRedirect />} />
      {/* Production: tenant portal resolved by Host */}
      <Route path="/" element={<PortalEntry page="login" />} />
      <Route path="/login" element={<PortalEntry page="login" />} />
      <Route path="/register" element={<PortalEntry page="register" />} />
      <Route path="/forgot-password" element={<PortalEntry page="forgot" />} />
      <Route path="*" element={<PortalEntry page="login" />} />
    </Routes>
  );
}

function AdminAuthRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register/:slug" element={<RegisterRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function MainRoutes() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/organizations" element={<Organizations />} />
        <Route path="/organizations/:tid" element={<OrganizationDetail />} />
        <Route path="/billing" element={<SuperBilling />} />
        <Route path="/users" element={<Users />} />
        <Route path="/advertisers" element={<Advertisers />} />
        <Route path="/products" element={<Products />} />
        <Route path="/offers" element={<Offers />} />
        <Route path="/offers/:cardId/geo/:offerId" element={<OfferEditorPage />} />
        <Route path="/streams" element={<Streams />} />
        <Route path="/multilinks" element={<MultiLinks />} />
        <Route path="/facebook" element={<MetaIntegrations />} />
        <Route path="/requests" element={<Requests />} />
        <Route path="/logs" element={<Logs />} />
        <Route path="/clicks" element={<Clicks />} />
        <Route path="/conversions" element={<Conversions />} />
        <Route path="/statistics" element={<Statistics />} />
        <Route path="/finance" element={<Finance />} />
        <Route path="/wallets" element={<Wallets />} />
        <Route path="/withdrawals" element={<Navigate to="/wallets" replace />} />
        <Route path="/news" element={<Announcements />} />
        <Route path="/announcements" element={<Announcements />} />
        <Route path="/postbacks" element={<Postbacks />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/system" element={<System />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function HoldScreen() {
  const { logout } = useAuth();
  const { data } = useQuery({
    queryKey: ["auth-hold-context"],
    queryFn: async () => (await http.get("/auth/hold-context")).data,
  });
  const back = async () => {
    const slug = data?.portal_slug;
    await logout();
    // Return to the SAME partner portal's login (never the generic platform login).
    window.location.assign(slug ? `/portal/${slug}/login` : "/login");
  };
  return <PendingView result={data || { message: "Your application is awaiting approval." }} onBack={back} />;
}

function Root() {
  const { user } = useAuth();
  useLocation(); // re-detect portal/admin mode on every navigation
  const { data: hostsData, isLoading: hostsLoading } = useQuery({
    queryKey: ["public-platform-hosts"],
    queryFn: async () => (await http.get("/public/platform-hosts")).data,
    staleTime: 0,
  });
  const { data: maint } = useQuery({
    queryKey: ["public-maintenance"],
    queryFn: async () => (await http.get("/public/maintenance")).data,
    refetchInterval: 30000,
  });
  const portal = detectPortal(hostsData?.hosts || []);
  if (user === null || hostsLoading) return <Loader />;

  const isSuper = (user && user.role === "super_admin") || (user && (user.permissions || []).includes("*"));
  const mEnabled = maint?.enabled;

  if (user === false) {
    if (portal.isPortal) {
      if (mEnabled && !maint.allow_portal) return <Maintenance message={maint.message} until={maint.until} />;
      return <PortalRoutes />;
    }
    return <AdminAuthRoutes />; // login/landing stays reachable so Super Admin can sign in
  }

  // A Hold account never reaches the cabinet — whatever login path created the session, always
  // show the same waiting screen until an admin flips the account to Active.
  if (user.status === "hold") return <HoldScreen />;

  // Authenticated admin app: gate everyone except Super Admin (and Tenant Admin if allowed).
  if (mEnabled && !isSuper && !(user.role === "tenant_admin" && maint.allow_tenant_admin)) {
    return <Maintenance message={maint.message} until={maint.until} />;
  }
  return <MainRoutes />;
}

export default function App() {
  return (
    <BrowserRouter>
      <BrandApplier />
      <Root />
    </BrowserRouter>
  );
}
